/* The enabled set — STORY-041.
 *
 * The only screen in the product where the full platform set is searched.
 * Everywhere else offers the enabled handful.
 *
 * ── deliberately NOT a combobox ──────────────────────────────────────────────
 * `person-picker` is an ARIA combobox because it SETS a form value. This sets
 * nothing; it issues commands — enable this, disable that. So it is a plain
 * search input filtering an ordinary list whose rows carry real buttons and
 * switches, with the match count in a live region. No `role="listbox"`, no
 * `role="option"`, no `aria-activedescendant`.
 *
 * That is not a style preference. Interactive controls nested inside
 * `role="option"` are unreachable for most screen-reader users, and it is the
 * defect that makes most transfer-list widgets unusable. The result is
 * substantially simpler than `person-picker` rather than a variant of it.
 *
 * ── one component, two sets ──────────────────────────────────────────────────
 * Currencies and timezones behave identically, so everything that differs
 * between them is a lookup in `COPY` and in the options passed in. The renderer
 * never branches on which one it is showing — two branches would be two designs
 * that must never disagree.
 */

import type { EnabledKind, EnabledValue } from '@eventuley/contracts';
import { type CurrencyCode, minorUnitDigits } from '@eventuley/domain';
import {
  filterCurrencies,
  filterTimezones,
  listCurrencies,
  listTimezones,
  type MessageKey,
} from '@eventuley/i18n';
import { useMemo, useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout } from '../../../shared/ui/index.js';
import { useDisableValue, useEnableValue } from '../api/index.js';

/**
 * Everything that differs between the two sets, as keys rather than branches.
 *
 * Named explicitly rather than built as `settings.${kind}.heading` —
 * `i18n.md` forbids a dynamically constructed key, because it defeats
 * extraction and unused-key detection. An explicit map is the stated
 * alternative.
 */
const COPY = {
  currency: {
    heading: 'settings.currencies.heading',
    searchLabel: 'settings.currencies.search_label',
    placeholder: 'settings.currencies.search_placeholder',
    hint: 'settings.currencies.hint',
    empty: 'settings.currencies.empty',
    countEnabled: 'settings.currencies.count_enabled',
    countMatches: 'settings.currencies.count_matches',
    own: 'settings.currencies.own',
    ownLocked: 'settings.currencies.own_locked',
    lastLocked: 'settings.currencies.last_locked',
    memberNote: 'settings.currencies.member_note',
  },
  timezone: {
    heading: 'settings.timezones.heading',
    searchLabel: 'settings.timezones.search_label',
    placeholder: 'settings.timezones.search_placeholder',
    hint: 'settings.timezones.hint',
    empty: 'settings.timezones.empty',
    countEnabled: 'settings.timezones.count_enabled',
    countMatches: 'settings.timezones.count_matches',
    own: 'settings.timezones.own',
    ownLocked: 'settings.timezones.own_locked',
    lastLocked: 'settings.timezones.last_locked',
    memberNote: 'settings.timezones.member_note',
  },
} as const satisfies Record<EnabledKind, Record<string, MessageKey>>;

/* Searching the whole platform set can match hundreds of rows. Rendering them
 * all measures the browser rather than serving the reader, and the answer to
 * "too many results" is a narrower query — which the line under the list says
 * out loud rather than silently truncating. */
const MAX_RESULTS = 40;

export interface EnabledSetProps {
  readonly kind: EnabledKind;
  readonly organisationId: string;
  /** The workspace's own value. Can never be disabled. */
  readonly own: string;
  readonly enabled: readonly EnabledValue[];
  /**
   * Why the set could not be read, if it could not.
   *
   * Passed in rather than read here, because one request answers both sections
   * and two hooks would be two requests. It exists at all because without it a
   * failed read is INDISTINGUISHABLE from a workspace that offers nothing: the
   * data is undefined, the list is empty, and the screen says "Currencies
   * enabled: 0" with no error — the plausible-empty-answer failure this
   * feature's own adapter warns about, reproduced one layer up.
   */
  readonly loadError?: unknown;
  /**
   * Whether the set is still being read.
   *
   * Without it this renders `enabled: 0` while the request is in flight — a
   * confident number that is wrong, on the screen whose entire job is to say
   * what the workspace offers. It is the same argument `loadError` already
   * makes: an unread set is not an empty one, and neither is a half-read one.
   * Found by walkthrough 2026-09-05, where it read 0 for about a second before
   * becoming 5.
   */
  readonly loading?: boolean;
  /** Whether the reader may change it. Presentation only — the server decides. */
  readonly canManage: boolean;
}

interface Option {
  readonly code: string;
  readonly name: string;
}

export function EnabledSet({
  kind,
  organisationId,
  own,
  enabled,
  loadError,
  loading = false,
  canManage,
}: EnabledSetProps) {
  const t = useTranslate();
  const { formatLocale } = useI18n();
  const copy = COPY[kind];

  const [query, setQuery] = useState('');
  /* The one outstanding confirmation, or null. Held here rather than per row so
   * two cannot be open at once, and so re-rendering the list underneath it can
   * neither lose it nor produce a second one. */
  const [confirming, setConfirming] = useState<string | null>(null);

  const enable = useEnableValue(organisationId);
  const disable = useDisableValue(organisationId);

  /* Read from the platform once per locale, not per keystroke: naming ~420
   * zones is measurably slow and the answer does not change while the screen is
   * open. `i18n.md` forbids a committed list, so this is the only source.
   *
   * Held in its own memo rather than derived twice, because `filterTimezones`
   * takes `TimezoneOption`s and the display list takes `Option`s. Building the
   * second inside the search memo re-mapped and re-collated all ~420 on every
   * keystroke — which the first half of this comment claimed was avoided, and
   * was not, until review caught it on 2026-08-21. */
  const zones = useMemo(
    () => (kind === 'timezone' ? listTimezones(formatLocale) : []),
    [kind, formatLocale],
  );

  const all: readonly Option[] = useMemo(
    () =>
      kind === 'currency'
        ? listCurrencies(formatLocale).all
        : zones.map((zone) => ({
            code: zone.code,
            /* City and offset, because a bare identifier orders and reads by
             * continent. The offset is display only — it changes twice a year
             * and the identifier is the identity. */
            name: `${zone.city} · ${zone.offset}`,
          })),
    [kind, formatLocale, zones],
  );

  const nameOf = useMemo(() => {
    const names = new Map(all.map((option) => [option.code, option.name]));
    /* A value can be enabled and unknown to this runtime — an older build
     * spelled a renamed zone the other way. It still displays, under its own
     * identifier, rather than vanishing from the list that manages it. */
    return (code: string) => names.get(code) ?? code;
  }, [all]);

  const collator = useMemo(() => new Intl.Collator(formatLocale), [formatLocale]);
  const enabledCodes = useMemo(() => new Set(enabled.map((row) => row.value)), [enabled]);

  /* The workspace's own value first, then the rest by display name under
   * locale-aware collation — never byte order, which puts every accented name
   * after Z. */
  const held = useMemo(
    () =>
      [...enabled].sort((a, b) =>
        a.value === own
          ? -1
          : b.value === own
            ? 1
            : collator.compare(nameOf(a.value), nameOf(b.value)),
      ),
    [enabled, own, collator, nameOf],
  );

  const matching = useMemo(() => {
    if (query.trim() === '') return null;
    const options =
      kind === 'currency'
        ? filterCurrencies(all, query, formatLocale)
        : filterTimezones(zones, query, formatLocale).map((zone) => ({
            code: zone.code,
            name: `${zone.city} · ${zone.offset}`,
          }));

    return {
      on: held.filter((row) => options.some((option) => option.code === row.value)),
      off: options
        .filter((option) => !enabledCodes.has(option.code))
        .sort((a, b) => collator.compare(a.name, b.name)),
    };
  }, [query, kind, all, zones, formatLocale, held, enabledCodes, collator]);

  const failure = enable.error ?? disable.error;
  const total = matching ? matching.on.length + matching.off.length : held.length;

  /* An unreadable set is not an empty one, and the difference has to be on the
   * screen. Nothing else renders: what is enabled is unknown, so offering to
   * manage it would be offering to act on a list that is not the truth. */
  /* Before the error branch, because a request in flight has not failed yet
   * and must not borrow the failure's wording. */
  if (loading) {
    return (
      <section className="settings__section enabled-set">
        <h2>{t(copy.heading)}</h2>
        <p className="field__hint">{t('common.loading')}</p>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="settings__section enabled-set">
        <h2>{t(copy.heading)}</h2>
        <Callout tone="danger">
          {loadError instanceof NetworkError
            ? t('error.network')
            : t('settings.enabled.unreadable')}
        </Callout>
      </section>
    );
  }

  return (
    <section className="settings__section enabled-set">
      <h2>{t(copy.heading)}</h2>

      {canManage ? null : <p className="field__hint">{t(copy.memberNote)}</p>}

      <label className="field enabled-set__search">
        {/* A real label, never a placeholder standing in for one. */}
        <span className="field__label">{t(copy.searchLabel)}</span>
        <input
          className="field__control"
          type="search"
          value={query}
          placeholder={t(copy.placeholder)}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>

      {/* The match count, announced. Without it a screen-reader user types into
          a box and is told nothing about what happened. */}
      <p className="enabled-set__count" role="status">
        {matching
          ? t(copy.countMatches, { query: query.trim(), count: String(total) })
          : t(copy.countEnabled, { count: String(held.length) })}
      </p>

      {/* What the list CONTROLS, taught at the moment it is least obvious: a
          workspace with one value looks like a screen that does nothing. */}
      {!matching && held.length === 1 ? <p className="enabled-set__hint">{t(copy.hint)}</p> : null}

      {failure ? (
        <Callout tone="danger">
          {failure instanceof NetworkError
            ? t('error.network')
            : failure instanceof ApiError && failure.code === 'conflict'
              ? /* Branched on the CODE and rendered from the catalogue — never
                   the server's `message`. That message is a hardcoded English
                   string, and putting it on screen would have been the only
                   place in this client that shows one, handing a Turkish reader
                   English at the one moment they are being refused. The two
                   conflicts are distinguishable here because the client knows
                   which value it just tried. */
                t(disable.variables?.value === own ? copy.ownLocked : copy.lastLocked)
              : t('settings.enabled.failed')}
        </Callout>
      ) : null}

      {matching && total === 0 ? (
        <p className="enabled-set__empty">{t(copy.empty, { query: query.trim() })}</p>
      ) : null}

      {(matching ? matching.on : held).length > 0 ? (
        <>
          <h3 className="enabled-set__group">{t('settings.enabled.group_on')}</h3>
          <ul className="row-list row-list--tight">
            {(matching ? matching.on : held).map((row) => (
              <Row
                key={row.value}
                code={row.value}
                name={nameOf(row.value)}
                kind={kind}
                inUse={row.inUse}
                own={own}
                lastOfKind={held.length === 1}
                canManage={canManage}
                confirming={confirming === row.value}
                busy={disable.isPending && disable.variables?.value === row.value}
                onAsk={() => {
                  disable.reset();
                  setConfirming(row.value);
                }}
                onCancel={() => setConfirming(null)}
                onDisable={() =>
                  disable.mutate(
                    { kind, value: row.value },
                    { onSuccess: () => setConfirming(null) },
                  )
                }
              />
            ))}
          </ul>
        </>
      ) : null}

      {matching && matching.off.length > 0 ? (
        <>
          {/* Headed separately, so a match that is already on shows its switch
              rather than offering a duplicate Add. */}
          <h3 className="enabled-set__group">{t('settings.enabled.group_off')}</h3>
          <ul className="row-list row-list--tight">
            {matching.off.slice(0, MAX_RESULTS).map((option) => (
              <li key={option.code} className="row">
                <span className="enabled-set__code">{option.code}</span>
                <span className="row__main">
                  <span className="row__title">{option.name}</span>
                  <span className="row__meta">{noteFor(kind, option.code, t)}</span>
                </span>
                <span className="row__actions">
                  {canManage ? (
                    <Button
                      variant="secondary"
                      busy={enable.isPending && enable.variables?.value === option.code}
                      onClick={() => {
                        enable.reset();
                        enable.mutate({ kind, value: option.code });
                      }}
                    >
                      {/* The accessible name carries the SUBJECT. Twelve
                          controls all named "Add" is the usual failure in a
                          list of commands. */}
                      {t('settings.enabled.add', { name: option.name })}
                    </Button>
                  ) : (
                    <span className="row__meta">{t('settings.enabled.owner_only')}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {matching.off.length > MAX_RESULTS ? (
            <p className="enabled-set__count">
              {t('settings.enabled.more', {
                count: String(matching.off.length - MAX_RESULTS),
              })}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/** The row's note: what makes this value special, or that nothing uses it. */
function noteFor(
  kind: EnabledKind,
  code: string,
  t: ReturnType<typeof useTranslate>,
  options: { own?: string; inUse?: number } = {},
): string {
  const parts: string[] = [];
  if (options.own === code) parts.push(t(COPY[kind].own));

  if (options.inUse !== undefined) {
    parts.push(
      options.inUse === 0
        ? t('settings.enabled.unused')
        : t('settings.enabled.used', { count: String(options.inUse) }),
    );
  }

  /* Minor-unit digits, shown only when they are NOT 2. The point is that the
   * count comes from the currency rather than from an assumption, so the
   * exceptions are what a reader needs to see — an Owner enabling JPY should
   * know why the amount field will behave differently. */
  if (kind === 'currency') {
    const digits = minorUnitDigits(code as CurrencyCode);
    if (digits === 0) parts.push(t('settings.enabled.decimals_none'));
    else if (digits !== 2) parts.push(t('settings.enabled.decimals', { count: String(digits) }));
  }

  return parts.join(' · ');
}

interface RowProps {
  readonly code: string;
  readonly name: string;
  readonly kind: EnabledKind;
  readonly inUse: number;
  readonly own: string;
  readonly lastOfKind: boolean;
  readonly canManage: boolean;
  readonly confirming: boolean;
  readonly busy: boolean;
  readonly onAsk: () => void;
  readonly onCancel: () => void;
  readonly onDisable: () => void;
}

function Row(props: RowProps) {
  const t = useTranslate();
  const { code, name, kind, inUse, own, lastOfKind, canManage, confirming, busy } = props;

  /* Two reasons a value can never be turned off: it is the workspace's own, or
   * it is the last one left. Something must always be pickable. */
  const isOwn = code === own;
  const locked = isOwn || lastOfKind;

  return (
    <li className="row">
      <span className="enabled-set__code">{code}</span>
      <span className="row__main">
        <span className="row__title">{name}</span>
        <span className="row__meta">{noteFor(kind, code, t, { own, inUse })}</span>
        {/* The reason, in TEXT beside the inoperable control — `design.md`
            rejects a greyed control with no explanation, and a tooltip is
            neither readable on a phone nor announced. */}
        {locked && canManage ? (
          <span className="row__meta">
            {isOwn ? t(COPY[kind].ownLocked) : t(COPY[kind].lastLocked)}
          </span>
        ) : null}

        {confirming ? (
          <Callout tone="warning">
            <strong>{t('settings.enabled.confirm_title', { name })}</strong>
            {/* The count IS the message: it says what survives. Nothing is
                deleted and nothing is rewritten — enablement governs the
                picker, never stored data. */}
            <p>{t('settings.enabled.confirm_body')}</p>
            <div className="btn-row">
              <Button busy={busy} onClick={props.onDisable}>
                {t('settings.enabled.confirm_yes')}
              </Button>
              <Button variant="ghost" onClick={props.onCancel}>
                {t('settings.enabled.confirm_no')}
              </Button>
            </div>
          </Callout>
        ) : null}
      </span>

      <span className="row__actions">
        {canManage ? (
          <label className="enabled-set__toggle">
            <input
              type="checkbox"
              role="switch"
              checked
              /* Explicit beside `checked`: overriding the implicit checkbox role
                 means the implicit checked state is no longer mapped, so a
                 screen reader announcing a switch has nothing to read. */
              aria-checked={true}
              disabled={locked || busy}
              aria-label={
                locked
                  ? t('settings.enabled.locked_label', { name })
                  : t('settings.enabled.toggle', { name })
              }
              onChange={() => {
                /* Confirm ONLY when the value is in use. Confirming a harmless,
                 * reversible action teaches people to dismiss dialogs without
                 * reading them. */
                if (inUse > 0) props.onAsk();
                else props.onDisable();
              }}
            />
            <span>{locked ? t('settings.enabled.always_on') : t('settings.enabled.on')}</span>
          </label>
        ) : (
          /* Read-only, and still readable: text rather than a disabled control,
             so nothing about it invites a click. */
          <span className="badge">{t('settings.enabled.on')}</span>
        )}
      </span>
    </li>
  );
}
